import { DropdownOptionModel } from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import { DropdownOptionGroup } from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React, { useMemo, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import { useQueryCanUseAIExpenseCategories } from 'src/queries/organizations/automationSettings/automationSettings.queries'
import {
  activeAlphabeticalExpenseCategoryLabelFiltersBody,
  useQueryExpenseCategoryLabels,
} from 'src/queries/organizations/expenseCategories.queries'
import { defaultQueryCacheOptions } from 'src/queries/queries.constants'
import { useQueryTransactionExpenseCategoryLabels } from 'src/queries/transactions.queries'
import { InfiniteScrollProps } from 'src/types'

interface UseExpenseCategoryDropdownOptionsParams {
  expenseCategoryId: string
  transactionId?: string
  search?: string
  freeTextAllowed?: boolean
  additionalKey?: string
}

interface UseExpenseCategoryDropdownOptionsResult {
  optionGroups: DropdownOptionGroup[]
  infiniteScrollProps: InfiniteScrollProps<string>
  isLoading: boolean
  searchQuery: string
  setSearchQuery: (query: string) => void
  suggestedOptionValues: Set<string>
}

export const useExpenseCategoryDropdownOptions = (
  params: UseExpenseCategoryDropdownOptionsParams,
): UseExpenseCategoryDropdownOptionsResult => {
  const { data: canUseAI, isLoading: isCanUseAILoading } =
    useQueryCanUseAIExpenseCategories()
  const useSuggestionsQuery = Boolean(canUseAI && params.transactionId)
  const translate = useTranslate()
  const [searchQuery, setSearchQuery] = useState<string>(params.search || '')

  // Use transaction-specific labels if transactionId provided and suggestions enabled
  const transactionLabelsQuery = useQueryTransactionExpenseCategoryLabels({
    expenseCategoryId: params.expenseCategoryId,
    transactionId: params.transactionId!,
    filters: {
      search: searchQuery,
      ...activeAlphabeticalExpenseCategoryLabelFiltersBody,
    },
    enabled: useSuggestionsQuery,
  })

  const labelsQuery = useQueryExpenseCategoryLabels({
    expenseCategoryId: params.expenseCategoryId,
    filters: {
      search: searchQuery,
      ...activeAlphabeticalExpenseCategoryLabelFiltersBody,
    },
    enabled: !useSuggestionsQuery,
    queryCacheOptions: defaultQueryCacheOptions,
    additionalKey: params.additionalKey,
  })

  const {
    data: labelsData,
    fetchNextPage: fetchMoreLabels,
    isFetching: labelsLoading,
    complete: labelsComplete,
  } = useSuggestionsQuery ? transactionLabelsQuery : labelsQuery

  const { optionGroups, suggestedOptionValues } = useMemo(() => {
    if (!labelsData)
      return { optionGroups: [], suggestedOptionValues: new Set<string>() }

    const suggestedValues = new Set<string>()

    const groups = labelsData.reduce(
      (acc, cur) => {
        if ('suggestionDetails' in cur && cur.suggestionDetails !== undefined) {
          suggestedValues.add(cur.id)
          acc[0].options.push({
            value: cur.id,
            label: cur.name,
            sublabel: cur.subName,
            disabled: !cur.active,
          })
        } else {
          acc[1].options.push({
            value: cur.id,
            label: cur.name,
            sublabel: cur.subName,
            disabled: !cur.active,
          })
        }
        return acc
      },
      [
        {
          id: 'suggested',
          heading: (
            <>
              {translate('Suggested')}
              <TooltipIcon>
                {translate(
                  'Suggestions from labels applied to similar past transactions',
                )}
              </TooltipIcon>
            </>
          ),
          options: [] as DropdownOptionModel<unknown>[],
        },
        {
          id: 'existing',
          heading: params.freeTextAllowed ? 'Existing Labels' : 'All Labels',
          options: [] as DropdownOptionModel<unknown>[],
        },
      ],
    )

    // Filter out duplicates from suggestions on the front-end
    const explicitSuggestionsGroups = groups?.map((group) => {
      if (group.id === 'existing') {
        return {
          ...group,
          options: group.options.filter(
            (option) =>
              !groups[0]?.options
                .map((opt) => opt.value)
                .includes(option.value),
          ),
        }
      }
      return group
    })

    return {
      optionGroups: explicitSuggestionsGroups?.filter(
        (group) => group.options.length > 0,
      ),
      suggestedOptionValues: suggestedValues,
    }
  }, [labelsData, params.freeTextAllowed, translate])

  const infiniteScrollProps: InfiniteScrollProps<string> = useMemo(
    () => ({
      fetch: (filterInput, page) => {
        if (page === 0) {
          setSearchQuery(filterInput || '')
        } else {
          fetchMoreLabels()
        }
      },
      page: 0,
      loading: labelsLoading,
      complete: labelsComplete,
    }),
    [fetchMoreLabels, labelsLoading, labelsComplete],
  )

  return {
    optionGroups,
    infiniteScrollProps,
    isLoading: isCanUseAILoading || labelsLoading,
    searchQuery,
    setSearchQuery,
    suggestedOptionValues,
  }
}
